import { partial } from 'lodash';
import {
  ICalculation,
  ICustomer,
  IExternalPricing,
  ILead,
  IOrderQueryResult,
  IPagination,
  IPimSnapshot,
  MAKE,
  OrderStatus,
} from 'next-common';
import { ReactNode, useEffect, useState } from 'react';
import { maxExpiryLength, useOrderExpiryLength } from '../../../../common';
import { findExpiresAt } from '../../../../utils';
import { TrackAndTrace, VehicleOrder } from '../../../sales/sales-common/DynamicCols';
import { OrderCategory } from '../orderQuery.utils';
import {
  CANCEL_RESERVATION_TEXT,
  DELETE_OFFER_TEXT,
  RESUME_ORDER_TEXT,
  SHOW_OFFER_TEXT,
  customerFormatter,
  dateFormatter,
  dealerFormatter,
  expiryDateFormatter,
  makeIcon,
  menuButtonsFormatter,
  offerStatusFormatter,
  offertypeFormatter,
  reservationStatusFormatter,
  salesPersonFormatter,
} from './MyOrders.formatters';
import {
  EmptyComponentProps,
  IDynamicListColumn,
  IDynamicListConfig,
  IDynamicListSort,
  LoadMoreComponentProps,
  TMenuItem,
} from 'types';
import { DynamicList, Icon, IconType, LoadingSpinner } from '@repo/ui/components';

const myOfferSort: IDynamicListSort[] = [{ key: 'createdAt', reverse: true }];
const myOrderSort: IDynamicListSort[] = [{ key: 'signedAt', reverse: true }];

const getMenuConfig = (props: Partial<IMyOrdersProps>): IDynamicListConfig => {
  let colConfig: IDynamicListColumn[];
  let { onContinue, onCancelReservation, onDelete } = props;
  const { onSelect } = props;

  switch (props.category) {
    case OrderCategory.ORDER:
    case OrderCategory.STOCK:
    case OrderCategory.DEMO:
    case OrderCategory.CANCEL_ORDER:
      colConfig = myOrdersConfig;
      onContinue = null;
      onCancelReservation = null;
      onDelete = null;
      break;
    case OrderCategory.DELIVERED:
      colConfig = myDeliveredConfig;
      onContinue = null;
      onCancelReservation = null;
      onDelete = null;
      break;
    default:
      colConfig = myOffersConfig;
  }

  return {
    commonCellClassName: 'px-4',
    commonHeaderClassName: 'px-4',
    cols: [
      ...colConfig,
      {
        header: '',
        key: 'id',
        colWidth: '3rem',
        noSort: true,
        formatter: partial(
          menuButtonsFormatter,
          getMenuItems(props.category, onSelect, onContinue, onCancelReservation, onDelete),
          null
        ),
        headerClassName: '',
        cellClassName: '',
      },
    ],
    sortingIcon: {
      iconDropdownDown: IconType.NewNextArrowDown,
      iconDropdownUp: IconType.NewNextArrowUp,
      iconHeight: 24,
      iconWidth: 24,
      iconColor: 'var(--color-black)',
    },
  };
};

const getMenuItems = (type: OrderCategory, onSelect, onContinue, onCancelReservation, onDelete): TMenuItem[] => {
  switch (type) {
    case OrderCategory.ORDER:
    case OrderCategory.STOCK:
    case OrderCategory.DEMO:
    case OrderCategory.CANCEL_ORDER:
    case OrderCategory.DELIVERED:
      return [
        {
          icon: IconType.NewNextDocumentLines,
          text: 'Vis',
          onSelect: onSelect,
        },
      ];
    default:
      return [
        {
          icon: IconType.NewNextDocumentLines,
          text: SHOW_OFFER_TEXT,
          onSelect: onSelect,
        },
        {
          icon: IconType.NewNextHistory,
          text: RESUME_ORDER_TEXT,
          onSelect: onContinue,
        },
        {
          icon: IconType.NewNextCross,
          text: CANCEL_RESERVATION_TEXT,
          onSelect: onCancelReservation,
        },
        {
          icon: IconType.NewNextTrash,
          text: DELETE_OFFER_TEXT,
          onSelect: onDelete,
        },
      ];
  }
};

const myOffersConfig: IDynamicListColumn[] = [
  {
    header: 'Type',
    key: 'orderType',
    colWidth: 'minmax(5rem, 1fr)',
    formatter: offertypeFormatter,
    headerClassName: '',
    cellClassName: '',
  },
  {
    header: 'Status',
    key: 'status',
    colWidth: 'minmax(7rem, 1fr)',
    formatter: offerStatusFormatter,
    headerClassName: '',
    cellClassName: '',
  },
  {
    header: 'Reservasjon',
    key: 'reservationStatus',
    colWidth: 'minmax(8.5rem, 1fr)',
    formatter: reservationStatusFormatter,
    headerClassName: '',
    cellClassName: '',
  },
  {
    header: 'Kunde',
    key: 'customer',
    colWidth: 'minmax(12.25rem, 1fr)',
    formatter: customerFormatter,
    headerClassName: '',
    cellClassName: '',
  },
  {
    header: 'Merke',
    key: 'vehicleMake',
    colWidth: 'minmax(5.6rem, 1fr)',
    formatter: makeIcon,
    headerClassName: '',
    cellClassName: '',
  },
  {
    header: 'Modell',
    key: 'vehicleModelSeries',
    colWidth: 'minmax(5.9rem, 1fr)',
    headerClassName: '',
    cellClassName: '',
  },
  {
    header: 'T&T',
    key: 'trackAndTrace',
    colWidth: 'minmax(5rem, 1fr)',
    headerClassName: '',
    cellClassName: '',
  },
  {
    header: 'Serienr.',
    key: 'serialNumber',
    colWidth: 'minmax(7rem, 1fr)',
    headerClassName: '',
    cellClassName: '',
  },
  {
    header: 'Forhandler',
    key: 'soldByDealerId',
    colWidth: 'minmax(8rem, 1fr)',
    formatter: dealerFormatter,
    headerClassName: '',
    cellClassName: '',
  },
  {
    header: 'Selger',
    key: 'user',
    colWidth: 'minmax(11.25rem, 1fr)',
    formatter: salesPersonFormatter,
    headerClassName: '',
    cellClassName: '',
  },
  {
    header: 'Opprettet',
    key: 'createdAt',
    colWidth: 'minmax(7.5rem, 1fr)',
    formatter: dateFormatter,
    headerClassName: '',
    cellClassName: '',
  },
  {
    header: 'Utløper',
    key: 'actualExpiresAt',
    colWidth: 'minmax(7rem, 1fr)',
    formatter: expiryDateFormatter,
    headerClassName: '',
    cellClassName: '',
  },
];

const myOrdersConfig: IDynamicListColumn[] = [
  { header: 'Type', key: 'orderType', colWidth: 'minmax(5rem, 1fr)', formatter: offertypeFormatter },
  { header: 'Status', key: 'status', colWidth: 'minmax(7rem, 1fr)', formatter: offerStatusFormatter },
  { header: 'Kunde', key: 'customer', colWidth: 'minmax(12.25rem, 1fr)', formatter: customerFormatter },
  { header: 'Merke', key: 'vehicleMake', colWidth: 'minmax(5.6rem, 1fr)', formatter: makeIcon },
  { header: 'Modell', key: 'vehicleModelSeries', colWidth: 'minmax(5.9rem, 1fr)' },
  { header: 'T&T', key: 'trackAndTrace', colWidth: 'minmax(5rem, 1fr)' },
  { header: 'IFS Ordre', key: 'vehicleOrder', colWidth: 'minmax(8rem, 1fr)' },
  { header: 'Serienr.', key: 'serialNumber', colWidth: 'minmax(7rem, 1fr)' },
  { header: 'Forhandler', key: 'soldByDealerId', colWidth: 'minmax(8rem, 1fr)', formatter: dealerFormatter },
  { header: 'Selger', key: 'user', colWidth: 'minmax(11.25rem, 1fr)', formatter: salesPersonFormatter },
  { header: 'Opprettet', key: 'createdAt', colWidth: 'minmax(8rem, 1fr)', formatter: dateFormatter },
  { header: 'Signert', key: 'signedAt', colWidth: 'minmax(7rem, 1fr)', formatter: dateFormatter },
];

const myDeliveredConfig: IDynamicListColumn[] = [
  { header: 'Type', key: 'orderType', colWidth: 'minmax(6rem, 1fr)', formatter: offertypeFormatter },
  { header: 'Kunde', key: 'customer', colWidth: 'minmax(12.25rem, 1fr)', formatter: customerFormatter },
  { header: 'Merke', key: 'vehicleMake', colWidth: 'minmax(5.6rem, 1fr)', formatter: makeIcon },
  { header: 'Modell', key: 'vehicleModelSeries', colWidth: 'minmax(5.9rem, 1fr)' },
  { header: 'Serienr.', key: 'serialNumber', colWidth: 'minmax(7rem, 1fr)' },
  { header: 'Forhandler', key: 'soldByDealerId', colWidth: 'minmax(8rem, 1fr)', formatter: dealerFormatter },
  { header: 'Selger', key: 'user', colWidth: 'minmax(11.25rem, 1fr)', formatter: salesPersonFormatter },
  { header: 'Utlevert', key: 'deliveredAt', colWidth: 'minmax(7rem, 1fr)', formatter: dateFormatter },
];

interface IMyOrdersProps {
  data: IOrderQueryResult[];
  category: OrderCategory;
  loading: boolean;
  pagination: IPagination;

  onLoadMore(): void;

  onSelect(element, isElementClick: boolean): void;
  onContinue(element): void;
  onCancelReservation(element): void;
  onDelete(element): void;
}

export interface IMyOrdersItem {
  id: string;
  status: OrderStatus;
  ifsId: string;
  serialNumber: string | ReactNode;
  trackAndTrace: ReactNode;
  vehicleMake: string;
  vehicleModelSeries: string;
  vehicleOrder: ReactNode;
  actualExpiresAt: Date;
  vehicles: object[];
  customer: Partial<ICustomer>;
  chosenOffer: Partial<ICalculation>;
  lead?: ILead;
  pimSnapshot: Partial<IPimSnapshot>;
  externalPricing?: IExternalPricing;
  soldByDealerId?: string;
  dealerName?: string;
  isEditOrder?: boolean;
}

const validCategories = [OrderCategory.ORDER, OrderCategory.STOCK, OrderCategory.DEMO, OrderCategory.CANCEL_ORDER];

export const MyOrders = (props: IMyOrdersProps) => {
  const brandExpiryLength = useOrderExpiryLength();
  const [config, setConfig] = useState<IDynamicListConfig>();
  const [orders, setOrders] = useState<IMyOrdersItem[]>([]);
  const [sort, setSort] = useState<IDynamicListSort[]>(myOfferSort);

  const getBrandExpiryLength = (brandCode: string) => {
    const expiryObj = brandExpiryLength?.find((item) => item.brandCode === brandCode);
    return expiryObj?.length;
  };

  const getVehiclOrderType = (order: IOrderQueryResult) => {
    const vehicle = order.vehicles?.[0];
    if (vehicle) {
      if (vehicle.serialNo) return vehicle.serialNo;
      else return 'Fabrikkbestilt';
    } else return '-';
  };

  useEffect(() => {
    const { category, onSelect, onContinue, onCancelReservation, onDelete } = props;
    setSort(
      [OrderCategory.ORDER, OrderCategory.STOCK, OrderCategory.DEMO, OrderCategory.CANCEL_ORDER].includes(
        props.category
      )
        ? myOrderSort
        : myOfferSort
    );
    setConfig(getMenuConfig({ category, onSelect, onContinue, onCancelReservation, onDelete }));
  }, [props.category]);

  useEffect(() => {
    return setOrders(
      props.data?.map((order) => {
        const orderVahicalBrand = order.vehicles?.[0]?.make;
        const expiryLength = orderVahicalBrand ? getBrandExpiryLength(orderVahicalBrand) : maxExpiryLength;
        const vehicle = order.vehicles?.[0];
        const ifsId = order.orderReference?.ifsId;

        const serialNumber =
          [OrderCategory.ORDER, OrderCategory.STOCK, OrderCategory.DEMO, OrderCategory.CANCEL_ORDER].includes(
            props.category
          ) && !vehicle?.serialNo?.length ? (
            <VehicleOrder id={order.id} showVehicleId />
          ) : (
            getVehiclOrderType(order)
          );
        const trackAndTrace = (
          <TrackAndTrace
            serialNo={vehicle?.serialNo}
            make={order.pimSnapshot?.make as MAKE}
            series={order.pimSnapshot?.series}
            fetchPolicy={'network-only'}
          />
        );
        const vehicleMake = (vehicle?.make || order.pimSnapshot?.make) ?? null;
        const vehicleModelSeries = (order.pimSnapshot?.series || vehicle?.model?.name) ?? '-';
        const vehicleOrder = validCategories.includes(props.category) && <VehicleOrder id={order.id} />;
        const reservationStatus = order?.reserveDetails?.vehicleReserveStatus;
        const actualExpiresAt = findExpiresAt(order?.actualExpiresAt, order?.createdAt, expiryLength);
        const orderType = order?.lead?.orderType;
        const configId = order?.externalPricing?.configId;
        const soldByDealerId = order?.soldByDealerId;
        const dealerName = Array.isArray(order?.user?.employeeRoles)
          ? order.user.employeeRoles.find((role) => role?.dealer?.dealerId === soldByDealerId)?.dealer?.name
          : undefined;

        return {
          ...order,
          actualExpiresAt,
          ifsId,
          serialNumber,
          trackAndTrace,
          vehicleMake,
          vehicleModelSeries,
          vehicleOrder,
          reservationStatus,
          orderType,
          configId,
          soldByDealerId,
          dealerName,
        };
      })
    );
  }, [props.data, setOrders]);

  const EmptyFormatter = (props: EmptyComponentProps) => {
    const { isLoading } = props;
    return (
      <div className="w-full flex justify-center items-center bg-white border-t border-neutralGrey">
        {isLoading ? (
          <LoadingSpinner size={48} color="var(--color-markTurquoise)" />
        ) : (
          <span className="min-h-[3rem] flex items-center">Tom liste</span>
        )}
      </div>
    );
  };

  const LoadMoreFormatter = (props: LoadMoreComponentProps) => {
    const { loadingMore, onLoadMore } = props;
    return (
      <div className="w-full flex justify-center items-center py-4 bg-turquoise20 border-t border-neutralGrey rounded-b-lg">
        {!loadingMore ? (
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => !loadingMore && onLoadMore()}>
            <Icon
              height={24}
              width={24}
              icon={IconType.Plus}
              className="bg-turquoise rounded-full"
              onClick={onLoadMore}
            />
            <span className="font-extrabold leading-[175%]">Vis flere</span>
          </div>
        ) : (
          <LoadingSpinner size={25} />
        )}
      </div>
    );
  };

  return (
    <div className="pb-10">
      <DynamicList
        pagination={props.pagination}
        onLoadMore={
          orders?.length > 0 && props.pagination?.totalItems > props.pagination?.itemsPerPage * props.pagination?.page
            ? props.onLoadMore
            : null
        }
        data={orders}
        sort={sort}
        config={config}
        onElementClick={(element) => props.onSelect(element, true)}
        loading={props.loading}
        LoadMoreComponent={LoadMoreFormatter}
        EmptyComponent={EmptyFormatter}
        className="overflow-visible"
        rowClassName="hover:bg-white font-semibold leading-[175%]"
      />
    </div>
  );
};
